import Pagination from '../../../../components/shared/pagination';
import React, { useEffect, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import { FiMoreVertical } from 'react-icons/fi';
import Modal from '../../../../components/shared/modal';
import ConfirmationModal from '../../../../components/shared/confirmation-modal';
import { statusColors } from '../../../../utils/status-color-utils';
import { RxCaretSort } from 'react-icons/rx';
import { RiArrowUpSLine } from "react-icons/ri";
// import EditModal from '../../../../pages/services/edit-modal';
import Loader from '../../../../components/shared/loader';
import { formatUTCTimestampToLocal } from '../../../../utils/set-date-range-utils';
import UpdateStatusModal from '../../../../components/shared/update-status-modal';
import { deleteServiceConfig, getAllServiceConfigStatuses, getAllServiceConfigTypes, updateServiceConfigStatus } from '../../../../services/service-config-service';
import { camelCaseToWords } from '../../../../utils/structure-words-utils';
import EditModal from '../../../../pages/services/service-config-edit-modal';
import AuditLogModal from '../../../../components/shared/AuditLogModal';

const ServiceConfigs = ({
  loading,
  error,
  data,
  setData,
  globalFilter,
  onGlobalFilterChange,
  newlyAddedItem,
  setNewlyAddedItem,
  vehicles,
  accountId,
  fetchServiceConfig
}) => {

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sorting, setSorting] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isAuditLogModalOpen, setIsAuditLogModalOpen] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [typesOptions, setTypesOptions] = useState([]);

  useEffect(() => {
    const fetchGlobalData = async () => {
      if (!accountId) return;
      setLoadingStatus(true);

      try {
        const [statusRes, typeRes] = await Promise.all([
          getAllServiceConfigStatuses(accountId),
          getAllServiceConfigTypes(accountId),
        ]);
        if (statusRes.success) setStatusOptions(statusRes.data.data || []);
        if (typeRes.success) setTypesOptions(typeRes.data.data || []);
      } catch (err) {
        console.error("Failed to fetch global data", err);
        setStatusError('Failed to fetch global data');
      } finally {
        setLoadingStatus(false);
      }
    };

    fetchGlobalData();
  }, [accountId]);


  useEffect(() => {
    if (newlyAddedItem) {
      setSelectedRow(newlyAddedItem.data);
      setIsEditModalOpen(true);
      setNewlyAddedItem(null);
    }
  }, [newlyAddedItem, setNewlyAddedItem]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleActionClick = (id) => {
    setOpenMenuId(openMenuId === id ? null : id);
  };

  const closeMenu = () => {
    setOpenMenuId(null);
  };

  const handleRowClick = (row, e) => {
    if (!e.target.closest('input[type="checkbox"]')) {
      setSelectedRow(row.original);
      setIsEditModalOpen(true);
    }
  };

  const handleConfirmAction = (action, row) => {
    setSelectedRow(row.original);
    if (action === 'Change Status') {
      setIsUpdateStatusModalOpen(true);
    } else {
      setConfirmAction(action);
      setIsConfirmModalOpen(true);
    }
    setErrorMessage(null);
    closeMenu();
  };


  const handleConfirm = async () => {
    if (!selectedRow || !confirmAction) return;

    if (confirmAction === 'delete') {
      try {
        setIsDeleting(true);
        const serviceConfId = selectedRow.serviceConfId;
        const res = await deleteServiceConfig(accountId, serviceConfId);

        if (res.status === 204) {
          setData(prevData => prevData.filter(item => item.serviceConfId !== serviceConfId));
          setIsConfirmModalOpen(false);
          setSelectedRow(null);
          setConfirmAction(null);
          setSuccessMessage('Service Config deleted successfully!');
        } else {
          setErrorMessage(res.data?.message || 'Failed to delete Service Config entry');
        }
      } catch (error) {
        console.error('Error deleting Service Config:', error);
        setErrorMessage('An error occurred while deleting the Service Config entry');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = () => closeMenu();
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Define columns with sorting configuration
  const columns = useMemo(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <div className="flex justify-center w-full">
            <input
              type="checkbox"
              checked={table.getIsAllRowsSelected()}
              onChange={(e) => {
                e.stopPropagation();
                table.getToggleAllRowsSelectedHandler()(e);
              }}
              className="w-4 h-4"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center w-full" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              onChange={(e) => {
                e.stopPropagation();
                row.getToggleSelectedHandler()(e);
              }}
              className="w-4 h-4"
            />
          </div>
        ),
        enableSorting: false,
        size: 50,
      },
      {
        accessorKey: 'vehicleId.name',
        header: 'Vehicle',
        enableSorting: true,
        cell: info => info.getValue(),
      },
      {
        accessorKey: 'serviceType',
        header: 'Service Type',
        enableSorting: true,
        cell: info => camelCaseToWords(info.getValue()),
      },
      {
        accessorKey: 'triggerType',
        header: 'Trigger Type',
        enableSorting: true,
        cell: info => camelCaseToWords(info.getValue()),
      },
      {
        accessorKey: 'start',
        header: 'Start',
        enableSorting: true,
        cell: info => {
          const value = info.getValue();

          // Detect ISO date string
          if (typeof value === 'string' && value.includes('T') && value.includes('Z')) {
            return formatUTCTimestampToLocal(value, 'yyyy-MM-dd hh:mm a');
          }

          return value;
        }
      },
      {
        accessorKey: 'period',
        header: 'Period',
        enableSorting: true,
      },

      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: true,
        cell: info => {
          const rawStatus = info.getValue();
          const status = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : 'unknown';
          const color = statusColors[status] || statusColors.default;
          const displayStatus = camelCaseToWords(info.getValue());
          return (
            <div className="flex justify-start">
              <span
                className="py-0.5 rounded-md text-sm font-medium capitalize text-center whitespace-nowrap"
                style={{
                  backgroundColor: color,
                  color: 'black',
                  border: `1px solid ${color}`,
                  width: '100px',
                  display: 'inline-block',
                  textAlign: 'center'
                }}
              >
                {displayStatus}
              </span>
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Action',
        cell: ({ row }) => {
          const rowId = row.id;

          return (
            <div className="relative flex justify-center w-full">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(rowId);
                }}
                className="p-1 rounded-full hover:bg-gray-200"
              >
                <FiMoreVertical className="text-gray-600 text-sm" />
              </button>

              {openMenuId === rowId && (
                <div
                  className={`absolute right-0 w-32 bg-white rounded-md shadow-md z-10 border border-gray-200 top-5`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => handleConfirmAction('Change Status', row)}
                  >
                    Change Status
                  </button>
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      setSelectedRow(row.original);
                      setIsAuditLogModalOpen(true);
                      closeMenu();
                    }}
                  >
                    Audit Log
                  </button>
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-red-600 hover:bg-gray-100"
                    onClick={() => handleConfirmAction('delete', row)}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          );
        },
        enableSorting: false,
        size: 50,
      },
    ],
    [openMenuId]
  );

  // Initialize the table
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    enableSortingRemoval: false,
    globalFilterFn: (row, columnId, filterValue) => {
      const searchLower = filterValue.toLowerCase();
      const rowValue = String(row.getValue(columnId)).toLowerCase();

      if (columnId === 'serviceTime') {
        return formatUTCTimestampToLocal(rowValue, 'yyyy-MM-dd hh:mm a').toLowerCase().includes(searchLower);
      } else if (columnId.includes('.')) {
        const [parent, child] = columnId.split('.');
        const value = row.original[parent]?.[child] || '';
        return String(value).toLowerCase().includes(searchLower);
      }

      return rowValue.includes(searchLower);
    },
  });

  return (
    <div className='w-full'>
      <div className="overflow-x-auto max-w-full pb-20">

        {successMessage && (
          <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-md text-sm text-center">
            {successMessage}
          </div>
        )}

        {(error || statusError) && (
          <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
            {error || statusError}
          </div>
        )}
        {
          (loading || loadingStatus) ? <Loader />
            :
            <table className="min-w-full text-sm border border-gray-200">
              <colgroup>
                <col style={{ width: '50px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ minWidth: '120px' }} />
                <col style={{ minWidth: '120px' }} />
                <col style={{ minWidth: '120px' }} />
                <col style={{ minWidth: '120px' }} />
                <col style={{ minWidth: '120px' }} />
                <col />
                <col />
                <col style={{ minWidth: '100px' }} />
              </colgroup>
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id} className="bg-gray-100 whitespace-nowrap capitalize text-left">
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="px-2 py-1.5 border-b capitalize"
                        onClick={header.column.getToggleSortingHandler()}
                        style={{
                          cursor: header.column.getCanSort() ? 'pointer' : 'default',
                          width: header.column.getSize(),
                        }}
                      >
                        <div className="flex items-center gap-1">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getCanSort() && (
                            <span className="text-black">
                              {{
                                asc: <RiArrowUpSLine className="text-base mt-0.5" />,
                                desc: <RiArrowUpSLine className="text-base rotate-180" />,
                              }[header.column.getIsSorted()] ?? (
                                  <RxCaretSort className="text-lg" />
                                )}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map(row => (
                    <tr
                      key={row.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={(e) => handleRowClick(row, e)}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          className="px-2 py-1 border-b whitespace-nowrap"
                          style={{ width: cell.column.getSize() }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
        }

      </div>

      <Modal
        isOpen={isEditModalOpen && selectedRow}
        onClose={() => setIsEditModalOpen(false)}
        modalClass={'xl:w-[40%] lg:w-[45%] md:w-[55%] sm:w-[70%] w-[90%] '}
      >
        <EditModal
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          setIsEditModalOpen={setIsEditModalOpen}
          isVehicleTab={true}
          isVehicleEditable={false}
          vehicles={vehicles}
          setData={setData}
          types={typesOptions}
        />
      </Modal>

      <Modal
        isOpen={isUpdateStatusModalOpen && selectedRow}
        onClose={() => {
          setIsUpdateStatusModalOpen(false);
          setSelectedRow(null);
        }}
        modalClass={'xl:!w-[28%] lg:!w-[35%] md:!w-[40%] sm:!w-[65%] !w-[90%] !px-0'}
      >

        <UpdateStatusModal
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          setData={setData}
          setIsUpdateStatusModalOpen={setIsUpdateStatusModalOpen}
          fetchData={fetchServiceConfig}
          fromVehiclesTab={true}
          accountId={accountId}
          modalTitle='Change Service Configuration Status'
          statusKey='status'
          statusOptions={statusOptions}
          updateStatusApi={updateServiceConfigStatus}
          uniqueIdKey='serviceConfId'
        />
      </Modal>

      <Modal
        isOpen={isConfirmModalOpen && selectedRow}
        onClose={() => setIsConfirmModalOpen(false)}
        modalClass={'xl:!w-[28%] lg:w-[35%] md:w-[40%] sm:w-[60%] w-[90%] '}
      >
        <ConfirmationModal
          confirmAction={confirmAction}
          setIsConfirmModalOpen={setIsConfirmModalOpen}
          handleConfirm={handleConfirm}
          loading={isDeleting}
          errorMessage={errorMessage}
        />
      </Modal>

      <Modal
        isOpen={isAuditLogModalOpen && selectedRow}
        onClose={() => {
          setIsAuditLogModalOpen(false);
          setSelectedRow(null);
        }}
        modalClass="xl:w-[60%] lg:w-[70%] md:w-[80%] sm:w-[90%] w-[95%]"
      >
        <AuditLogModal
          isOpen={isAuditLogModalOpen}
          onClose={() => {
            setIsAuditLogModalOpen(false);
            setSelectedRow(null);
          }}
          accountId={accountId}
          linkedType="confs"
          linkedId={selectedRow?.serviceConfId}
        />
      </Modal>

      <Pagination
        currentPage={currentPage}
        totalItems={table.getFilteredRowModel().rows.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(count) => {
          setItemsPerPage(count);
          setCurrentPage(1);
        }}
      />
    </div>
  )
}

export default ServiceConfigs;
